/**
 * Deploy 5 · A — Reembolsar (Stripe) / Anular cobro (efectivo) una cita pagada.
 * Regla founder: el cliente recibe bruto − Stripe − Ziyup; nunca el 100 %.
 */
import { useEffect, useState } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { bookingApi } from '@/lib/bookingApi';
import { tr } from '@/contexts/LanguageContext';
import { fmtMoney } from '@/utils/money';

const money = (v) => fmtMoney(v);

const Line = ({ label, value, strong = false, testId }) => (
  <div className={`flex justify-between text-sm ${strong ? 'font-semibold text-foreground pt-1.5 border-t border-border' : 'text-muted-foreground'}`}>
    <span>{label}</span><span className="tabular-nums" data-testid={testId}>{value}</span>
  </div>
);

export const RefundDialog = ({ bookingId, open, onOpenChange, onDone }) => {
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !bookingId) return undefined;
    let cancelled = false;
    setQuote(null);
    bookingApi.refundPreview(bookingId).then((q) => { if (!cancelled) setQuote(q); }).catch((e) => {
      if (!cancelled) { toast.error(e?.message || tr("No pudimos calcular el reembolso")); onOpenChange(false); }
    });
    return () => { cancelled = true; };
  }, [open, bookingId]); // eslint-disable-line react-hooks/exhaustive-deps

  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await bookingApi.refundBooking(bookingId);
      const cb = (res?.clawbacks || []).length;
      toast.success(res?.kind === 'cash_void'
        ? tr("Cobro en efectivo anulado — la cita quedó cancelada")
        : tr("Reembolso de {0} enviado al cliente{1}", money(res?.refunded_amount), cb ? ' · parte quedó como deuda a descontar' : ''));
      onOpenChange(false);
      onDone?.(res);
    } catch (e) {
      toast.error(e?.message || tr("No se pudo procesar el reembolso"));
    } finally {
      setBusy(false);
    }
  };

  const isCash = quote?.kind === 'cash_void';
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="bg-card border-border" data-testid="refund-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <RotateCcw className="w-4 h-4 text-primary" /> {isCash ? tr("¿Anular el cobro en efectivo?") : tr("¿Reembolsar esta cita?")}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-left">
              {!quote ? (
                <div className="py-4 text-center"><div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" /></div>
              ) : isCash ? (
                <p className="text-sm text-muted-foreground" data-testid="refund-cash-note">{tr("Se anula el registro de pago de {0} y la cita pasa a cancelada. No hay dinero que devolver por Stripe — el efectivo lo devuelves tú en persona.", money(quote.cash_voided || quote.gross))}</p>
              ) : (
                <div className="bg-background border border-border rounded-xl p-3 space-y-1" data-testid="refund-breakdown">
                  <Line label={tr("Bruto cobrado")} value={money(quote.gross)} testId="refund-gross" />
                  <Line label={tr("− Comisión Stripe (no reembolsable)")} value={`−${money(quote.stripe_fee)}`} testId="refund-stripe" />
                  <Line label={tr("− Comisión Ziyup (no reembolsable)")} value={`−${money(quote.platform_fee)}`} testId="refund-lineage" />
                  <Line label={tr("Devuelve al cliente")} value={money(quote.refund_amount)} strong testId="refund-amount" />
                  {quote.cash_voided > 0 && <Line label={tr("Efectivo anulado (sin Stripe)")} value={money(quote.cash_voided)} testId="refund-cash-voided" />}
                </div>
              )}
              {quote?.post_payout && (
                <p className="text-xs text-amber-300 flex items-start gap-1.5 bg-amber-500/10 border border-amber-500/40 rounded-lg p-2" data-testid="refund-post-payout-warning">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{tr("El dinero ya fue transferido ({0}). Se intentará revertir la transferencia; si no hay saldo, quedará como deuda que se descuenta de los próximos pagos de esa parte.", quote.payouts.map((p) => `${p.payee_role === 'shop' ? 'barbería' : 'barbero'} ${money(p.amount)}`).join(' · '))}</p>
              )}
              <p className="text-[11px] text-muted-foreground">{tr("Reembolso total o nada; la cita quedará cancelada. Esta acción no se puede deshacer.")}</p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full" data-testid="refund-cancel-btn">{tr("Volver")}</AlertDialogCancel>
          <AlertDialogAction onClick={(e) => { e.preventDefault(); confirm(); }} disabled={!quote || busy}
            className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="refund-confirm-btn">
            {busy ? tr("Procesando…") : isCash ? tr("Anular cobro") : tr("Reembolsar {0}", quote ? money(quote.refund_amount) : '')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
